import { COMMA, ENTER } from '@angular/cdk/keycodes';
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatAutocompleteSelectedEvent, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatChipInputEvent } from '@angular/material/chips';
import { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { SubscriptionManager } from 'src/app/core/services/subscription-manager.service';

interface IContentTagsFormFieldComponentTag {
  id?: string;
  name: string;
}

@Component({
    selector: 'app-content-tags-form-field',
    templateUrl: './content-tags-form-field.component.html',
    styleUrls: ['./content-tags-form-field.component.scss'],
    standalone: false
})
export class ContentTagsFormFieldComponent implements OnInit, OnDestroy {
  @Input() placeholder = "";
  @Input() label = "";
  /**
   * Selected tags
   */
  @Input() tags: IContentTagsFormFieldComponentTag[] = [];

  /**
   * List with all the available tags
   */
  @Input() allTags: IContentTagsFormFieldComponentTag[] = [];

  /**
   * Reference to the input field
   */
  @ViewChild('tagInput') tagInput: ElementRef<HTMLInputElement>;

  /**
   * Reference to the autocomplete trigger
   */
  @ViewChild(MatAutocompleteTrigger, { static: false }) autoCompleteTrigger: MatAutocompleteTrigger;

  /**
   * Emits when the tags change. The `tags` property will be mutated before this event is emitted.
   */
  @Output() public readonly onChange = new EventEmitter<void>();

  /**
   * Disables the input field
   */
  @Input() disabled = false;

  /**
   * Set of tags to paint in red
   */
  @Input() duplicatedTagNames: string[] | null = null;

  public value: FormControl<string>;
  public readonly autoCompletionTags: Observable<
    IContentTagsFormFieldComponentTag[]
  >;
  public readonly separatorKeysCodes = [ENTER, COMMA] as const;
  private readonly subscriptionManager = new SubscriptionManager();

  constructor() {
    this.value = new FormControl('');
    this.autoCompletionTags = this.value.valueChanges.pipe(
      startWith(null),
      map((value) => {
        if (typeof value === 'string') {
          return this.allTags.filter((tag) =>
            tag.name.toLowerCase().includes(value.toLowerCase())
          );
        }
        return this.allTags;
      })
    );
    /**
     * sort tags by name when onChange is emitted
     */
    this.subscriptionManager.add(this.onChange.subscribe(() => {
      this.tags.sort((t1, t2) => t1.name.localeCompare(t2.name));
    }));
  }

  ngOnInit(): void {
  }

  ngOnDestroy() {
    this.subscriptionManager.unsubscribe();
  }

  remove(tag: IContentTagsFormFieldComponentTag) {
    const i = this.tags.indexOf(tag);
    if (i === -1) {
      console.error('Tried to remove a tag that does not exist');
      return;
    }
    this.tags.splice(i, 1);
    this.onChange.emit();
  }

  add(tag: IContentTagsFormFieldComponentTag) {
    if (!tag.name.trim().length) {
      return;
    }

    if (!this.tags.some((t) => t.name === tag.name)) {
      const existingTag = this.allTags.find((t) => t.name === tag.name) ?? tag;
      this.tags.push(existingTag);
      this.onChange.emit();
    }

    /**
     * Clear the input field
     */
    this.tagInput.nativeElement.value = '';
    this.value.setValue('');
  }

  onAddTagEvent(e: MatChipInputEvent) {
    this.add({
      id: null,
      name: e.value,
    });
  }

  onSelectAutoCompletionTag(e: MatAutocompleteSelectedEvent) {
    this.add({
      id: e.option.value.id,
      name: e.option.value.name,
    });
  }

  onAutocompleteOpened() {
    // Locks mat-autocomplete menu overlay to prevent the user from interacting with other scrollable elements
    (this.autoCompleteTrigger as any)._overlay._overlayContainer._containerElement.style.pointerEvents = 'auto';
  }

  onAutocompleteClosed() {
    (this.autoCompleteTrigger as any)._overlay._overlayContainer._containerElement.style.pointerEvents = '';
  }
}
